<template>
  <div ref="container" />
</template>

<script lang="ts">
let loadPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve) => {
    const existing = document.querySelector(
      'script[src*="challenges.cloudflare.com/turnstile"]'
    );

    if (existing) {
      // Script tag exists but hasn't loaded yet
      existing.addEventListener("load", () => resolve());
      return;
    }

    window.onloadTurnstileCallback = () => resolve();
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback";
    script.async = true;
    script.defer = true;

    document.head.appendChild(script);
  });

  return loadPromise;
}
</script>

<script setup lang="ts">
import { useTemplateRef, watch } from "vue";

const props = defineProps<{
  action?: string;
  cData?: string;
  execution?: "render" | "execute";
  theme?: "light" | "dark" | "auto";
  language?: string;
  tabindex?: number;
  size?: "normal" | "flexible" | "compact";
  retry?: "auto" | "never";
  retryInterval?: number;
  refreshExpired?: "auto" | "manual" | "never";
  refreshTimeout?: "auto" | "manual" | "never";
  appearance?: "always" | "execute" | "interaction-only";
  feedbackEnabled?: boolean;
}>();

const emit = defineEmits<{
  callback: [response: string];
  errorCallback: [response: string];
  expiredCallback: [];
}>();

const container = useTemplateRef("container");

watch(container, async (node, _prevNode, onCleanup) => {
  if (!node) {
    return;
  }

  const controller = new AbortController();
  const signal = controller.signal;

  onCleanup(() => {
    controller.abort();
  });

  await loadScript();
  if (signal.aborted) {
    return;
  }

  const id = window.turnstile!.render(node, {
    sitekey: "0x4AAAAAABrWC8POBFndQnsz",
    callback: (response: string) => emit("callback", response),
    "expired-callback": () => emit("expiredCallback"),
    "error-callback": (response: string) => emit("errorCallback", response),
    action: props.action,
    cData: props.cData,
    execution: props.execution ?? "render",
    theme: props.theme ?? "auto",
    language: props.language ?? "auto",
    tabindex: props.tabindex ?? 0,
    size: props.size,
    retry: props.retry,
    "retry-interval": props.retryInterval,
    "refresh-expired": props.refreshExpired,
    "refresh-timeout": props.refreshTimeout,
    appearance: props.appearance,
    "feedback-enabled": props.feedbackEnabled,
  });

  signal.addEventListener("abort", () => {
    window.turnstile!.remove(id);
  });
});
</script>
